import type {
  AutoAllocatePreview,
  AutoAllocatePreviewRequest,
  CreateReceiptRequest,
  OpenInvoice,
  ReceiptResponse,
  SetAllocationsRequest,
  UpdateReceiptRequest,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { PARTNERS_KEY } from '@/features/partners/api';
import { INVOICES_KEY } from '@/features/sales/api';
import { throwApiError } from '@/lib/server-messages';

/** FIN-03 data layer — same shape as partners/sales. */

export const RECEIPTS_KEY = ['receipts'] as const;

export interface ReceiptListFilters {
  partnerId?: string | undefined;
  status?: ReceiptResponse['status'] | undefined;
  /** RV-01 — the register's search box (number, partner, reference). */
  q?: string | undefined;
}

/** FIN-03's register, one page at a time (NFR-42). Both filters run in SQL. */
export function useReceipts(
  filters: ReceiptListFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...RECEIPTS_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.receipts.list({
        query: {
          ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.q ? { q: filters.q } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useReceipt(receiptId: string | undefined) {
  return useQuery({
    queryKey: [...RECEIPTS_KEY, 'one', receiptId],
    enabled: Boolean(receiptId),
    queryFn: async (): Promise<ReceiptResponse> => {
      const response = await api.receipts.get({ params: { receiptId: receiptId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * The customer's open POSTED invoices, oldest first (R9, Q-FIN-03-4), ONE
 * PAGE AT A TIME (NFR-42).
 *
 * The page is a parameter rather than something this hook decides, because
 * the editor keeps it in the URL (`?invoicePage=`, PAGINATION.md §3 step 12 —
 * the voucher screen has other blocks). `keepPreviousData` so turning a page
 * updates the grid without the strip blinking through its pending state.
 *
 * NOTHING FOLDS OVER THIS. Auto-allocate asks `useAutoAllocatePreview` /
 * `useAutoAllocate`, which walk the whole open set server-side, and the SAVE
 * payload is built from the editor's own `alloc` — see `allocationBody()` in
 * the editor and §3 step 13 for what happens when it is not.
 */
export function useOpenInvoices(
  partnerId: string | null | undefined,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  /** The voucher's currency (2026-10-30): only invoices in it are open to it. Absent = the partner's. */
  currency?: string | undefined,
) {
  return useQuery({
    queryKey: [...RECEIPTS_KEY, 'open-invoices', partnerId, page, pageSize, currency ?? null],
    enabled: Boolean(partnerId),
    queryFn: async (): Promise<{
      invoices: OpenInvoice[];
      total: number;
      page: number;
      pageSize: number;
    }> => {
      const response = await api.receipts.openInvoices({
        query: { partnerId: partnerId!, page, pageSize, ...(currency ? { currency } : {}) },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useCreateReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateReceiptRequest): Promise<ReceiptResponse> => {
      const response = await api.receipts.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY }),
  });
}

export function useUpdateReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateReceiptRequest): Promise<ReceiptResponse> => {
      const response = await api.receipts.update({ params: { receiptId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY }),
  });
}

export function useSetAllocations(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetAllocationsRequest): Promise<ReceiptResponse> => {
      const response = await api.receipts.setAllocations({ params: { receiptId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY }),
  });
}

/** BR-FIN-03-10 — oldest first; a zero amount fills all and writes back. */
export function useAutoAllocate(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ReceiptResponse> => {
      const response = await api.receipts.autoAllocate({ params: { receiptId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY }),
  });
}

/**
 * BR-FIN-03-10 for a voucher that has not been saved yet.
 *
 * This replaces `autoAllocateOldestFirst` on the /new path. The browser fold
 * had to read the customer's WHOLE open set to be right, which is what kept
 * `GET /api/receipts/open-invoices` unpaged: a page of that list folds to a
 * wrong total rather than to a missing row. The walk is the server's now, so
 * the spread is computed over every open invoice whether or not the grid is
 * showing them.
 *
 * A blank or half-typed amount box sends NO amount, which is the server's
 * "pay everything off" shortcut — the same reading the fold gave those cases,
 * so the button behaves identically for the user.
 */
export function useAutoAllocatePreview() {
  return useMutation({
    mutationFn: async (input: AutoAllocatePreviewRequest): Promise<AutoAllocatePreview> => {
      const response = await api.receipts.autoAllocatePreview({ body: input });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useDeleteReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.receipts.remove({ params: { receiptId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY }),
  });
}

/** Draft → Posted: invoice balances and the customer's total move (§12). */
export function usePostReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ReceiptResponse> => {
      const response = await api.receipts.post({ params: { receiptId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY });
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
    },
  });
}

/**
 * BR-FIN-03-17 / R8 — the posted-voucher correction: the ledger flips the
 * voucher's own entry (exchange difference included) and the reversed_at
 * stamp restores every allocated invoice's balance, so invoices and partner
 * totals must refetch exactly as posting makes them.
 */
export function useReverseReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<ReceiptResponse> => {
      const response = await api.receipts.reverse({ params: { receiptId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY });
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
    },
  });
}

/** Draft → Cancelled, the only other transition (BR-FIN-03-17). */
export function useCancelReceipt(receiptId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /** GEN-59: the cancel carries its typed reason. */
    mutationFn: async (reason: string): Promise<ReceiptResponse> => {
      const response = await api.receipts.cancel({ params: { receiptId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY }),
  });
}
